import { FAMILY_RANDOM_EVENTS } from "../familyEvents.ts";
import { FAMILY_EVENT_EXPANSION } from "../familyEventExpansion.ts";
import { GENERATION_ARCS, RANDOM_EVENTS, type Stats } from "../gameData.ts";
import type { EventChoice, EventDefinition, EventEffects, PersonalStats } from "./types.ts";
import { semanticLifeTheme } from "./eventThemes.ts";

const LEGACY_MONEY_TO_HOUSEHOLD_ASSETS = 0.25;

const personalStats = (effects: Partial<Stats> = {}): Partial<PersonalStats> => ({
  ...(effects.stamina === undefined ? {} : { stamina: effects.stamina }),
  ...(effects.intellect === undefined ? {} : { intellect: effects.intellect }),
  ...(effects.charm === undefined ? {} : { charm: effects.charm }),
  ...(effects.empathy === undefined ? {} : { empathy: effects.empathy }),
  ...(effects.luck === undefined ? {} : { luck: effects.luck }),
  ...(effects.health === undefined ? {} : { health: effects.health }),
});

const adaptedEffects = (effects: Partial<Stats> = {}, extra: Partial<Stats> = {}): EventEffects => ({
  stats: personalStats({
    stamina: Number(effects.stamina || 0) + Number(extra.stamina || 0),
    intellect: Number(effects.intellect || 0) + Number(extra.intellect || 0),
    charm: Number(effects.charm || 0) + Number(extra.charm || 0),
    empathy: Number(effects.empathy || 0) + Number(extra.empathy || 0),
    luck: Number(effects.luck || 0) + Number(extra.luck || 0),
    health: Number(effects.health || 0) + Number(extra.health || 0),
  }),
  assets: Math.round((Number(effects.money || 0) + Number(extra.money || 0)) * LEGACY_MONEY_TO_HOUSEHOLD_ASSETS),
});

function followUpFor(eventId: string, choiceId: string): EventChoice["followUp"] {
  if (eventId.includes("loan-request")) {
    if (choiceId.startsWith("lend-full")) return { kind: "family-loan", risk: 0.35 };
    if (choiceId.startsWith("lend-part")) return { kind: "family-loan", risk: 0.2 };
  }
  if (eventId.includes("guarantee")) {
    if (choiceId.startsWith("guarantee")) return { kind: "guarantee", risk: 0.38 };
  }
  if (eventId.includes("business") || eventId.includes("startup")) {
    if (["full-invest", "seed-money", "fund"].some((id) => choiceId.startsWith(id))) return { kind: "family-business", risk: 0.38, stages: 2 };
    if (["small-test", "milestone-fund", "limited-fund"].some((id) => choiceId.startsWith(id))) return { kind: "family-business", risk: 0.2, stages: 2 };
    if (["preserve-pension", "mentor-only", "no-fund"].some((id) => choiceId.startsWith(id))) return { kind: "family-business", risk: 0.58, stages: 2 };
  }
  if (eventId.includes("inheritance")) {
    if (choiceId.startsWith("legal-action")) return { kind: "inheritance", risk: 0.28 };
    if (choiceId.startsWith("mediate")) return { kind: "inheritance", risk: 0.38 };
    if (choiceId.startsWith("withdraw")) return { kind: "inheritance", risk: 0.08 };
  }
  return undefined;
}

const TARGET_CAREER_STATUSES: Partial<Record<string, Array<"employed" | "self-employed" | "unemployed">>> = {
  "partner-bonus": ["employed", "self-employed"],
  "partner-promotion": ["employed"],
  "partner-layoff": ["employed"],
  "partner-contract": ["employed", "self-employed"],
  "partner-career-break": ["employed", "self-employed"],
  "partner-relocation": ["employed"],
  "partner-startup": ["employed", "self-employed", "unemployed"],
  "partner-study": ["employed", "self-employed", "unemployed"],
};

function durableTargetCareerEffects(eventId: string, choiceId?: string): EventEffects {
  const coreId = eventId.replace(/^family-/, "");
  if (coreId === "partner-promotion") return { careerIncomeDelta: 18, careerLevelDelta: 1 };
  if (coreId === "partner-layoff") return { careerPauseYears: 1 };
  if (coreId === "partner-career-break") {
    if (choiceId === "support-break") return { careerPauseYears: 1 };
    if (choiceId === "parttime") return { career: { occupation: "彈性工時專員", status: "employed" }, careerIncomeDelta: -30 };
  }
  if (coreId === "partner-relocation") {
    if (choiceId === "move") return { careerIncomeDelta: 30, careerLevelDelta: 1 };
    if (choiceId === "commute") return { careerIncomeDelta: 12 };
  }
  if (coreId === "partner-startup") {
    if (choiceId === "fund") return { career: { industry: "新創事業", occupation: "共同創辦人", status: "self-employed", level: 1, experience: 0, annualIncome: 72 } };
    if (choiceId === "limited-fund") return { career: { industry: "自營事業", occupation: "小型事業經營者", status: "self-employed", level: 1, experience: 0, annualIncome: 82 } };
    if (choiceId === "no-fund") return { career: { industry: "新創事業", occupation: "籌備中的創業者", status: "self-employed", level: 1, experience: 0, annualIncome: 68 } };
  }
  if (coreId === "partner-study") {
    if (choiceId === "study-full") return { educationYears: 1, careerIncomeDelta: 18, careerLevelDelta: 1, careerPauseYears: 1 };
    if (choiceId === "study-evening") return { educationYears: 2, careerIncomeDelta: 10, careerLevelDelta: 1 };
  }
  return {};
}

export const ADAPTED_LIFE_EVENTS: EventDefinition[] = RANDOM_EVENTS
  .filter((event) => !["baby-first-word", "bad-loan"].includes(event.id))
  .map((event) => ({
    id: `life-${event.id}`,
    topic: event.tags[0] || "daily-life",
    ...semanticLifeTheme(event.id, event.tags[0] || "daily-life"),
    scope: "person",
    title: event.title,
    text: event.text,
    weight: Math.max(1, event.weight),
    once: true,
    maxOccurrences: 1,
    topicCooldownYears: 1,
    passive: true,
    targetRelation: event.targetRelation,
    targetConditions: event.targetRelation
      ? { minAge: event.targetMinAge, maxAge: event.targetMaxAge, alive: true }
      : undefined,
    conditions: {
      minAge: event.minAge,
      maxAge: event.maxAge,
      partnered: event.requiresPartner ? true : undefined,
      hasChildren: event.requiresChildren ? true : undefined,
      gender: event.genders?.length === 1 ? event.genders[0] : undefined,
    },
    choices: [{ id: "continue", label: "事件結果", text: event.text, effects: adaptedEffects(event.effects) }],
  }));

function familyTheme(coreId: string): Pick<EventDefinition, "themeKey" | "themeScope" | "themeCooldownYears" | "themeMaxOccurrences"> {
  if (coreId === "parent-guarantee") return { themeKey: "financial-guarantee", themeScope: "person", themeCooldownYears: 12, themeMaxOccurrences: 1 };
  if (/loan|debt|crowdfunding/.test(coreId)) return { themeKey: "family-financial-rescue", themeScope: "person", themeCooldownYears: 8, themeMaxOccurrences: 2 };
  if (/health|medical|surgery|hospital|therapy|treatment|care-rotation/.test(coreId)) return { themeKey: "family-health-care", themeScope: "person", themeCooldownYears: 5, themeMaxOccurrences: 3 };
  if (/inheritance|property/.test(coreId)) return { themeKey: "family-inheritance-property", themeScope: "person", themeCooldownYears: 9, themeMaxOccurrences: 2 };
  if (/scam|shopping-dispute|traffic-dispute|neighbor-boundary/.test(coreId)) return { themeKey: "family-dispute-protection", themeScope: "person", themeCooldownYears: 6, themeMaxOccurrences: 2 };
  if (/business|startup|side-offer/.test(coreId)) return { themeKey: "family-business-opportunity", themeScope: "person", themeCooldownYears: 7, themeMaxOccurrences: 2 };
  if (/lottery|prize|award|scholarship|windfall/.test(coreId)) return { themeKey: "family-good-fortune", themeScope: "person", themeCooldownYears: 4, themeMaxOccurrences: 4 };
  return {};
}

export const ADAPTED_FAMILY_EVENTS: EventDefinition[] = [...FAMILY_RANDOM_EVENTS, ...FAMILY_EVENT_EXPANSION].map((event) => {
  const coreId = event.id.replace(/^family-/, "");
  const passiveTargetCareerEffects = durableTargetCareerEffects(event.id);
  const choices: EventChoice[] = event.kind === "choice" && event.choices?.length
    ? event.choices.map((choice) => {
        const followUp = followUpFor(event.id, choice.id);
        const generatedMarriage = event.id === "family-child-wedding"
          ? { action: "marry-generated-partner" as const }
          : {};
        return {
          id: choice.id,
          label: choice.label,
          text: choice.text,
          effects: adaptedEffects(choice.effects),
          targetEffects: { ...adaptedEffects(choice.targetEffects), ...durableTargetCareerEffects(event.id, choice.id), ...generatedMarriage },
          ...(followUp ? { followUp } : {}),
          ...(choice.requires?.money
            ? { requires: { householdAssetsMin: Math.round(choice.requires.money * LEGACY_MONEY_TO_HOUSEHOLD_ASSETS) } }
            : {}),
        };
      })
    : [{
        id: "continue",
        label: "事件結果",
        text: event.text,
        effects: adaptedEffects(event.effects),
        targetEffects: { ...adaptedEffects(event.targetEffects), ...passiveTargetCareerEffects },
      }];
  return {
    id: `relative-${event.id}`,
    topic: `family-${event.relations[0] || "relative"}`,
    scope: "household",
    title: event.title,
    text: event.text,
    narrativeVariants: event.textVariants,
    weight: Math.max(1, event.weight),
    repeatable: true,
    maxOccurrences: event.id === "family-child-wedding" ? 6 : 1,
    cooldownYears: event.id === "family-child-wedding" ? 1 : event.kind === "passive" ? 6 : 8,
    topicCooldownYears: 2,
    ...familyTheme(coreId),
    passive: event.kind === "passive",
    targetRelation: event.relations[0],
    targetRelations: event.relations,
    targetConditions: {
      minAge: event.minAge,
      maxAge: event.maxAge,
      alive: true,
      ...(event.id === "family-child-wedding" ? { relationshipStage: "single" as const } : {}),
      ...(TARGET_CAREER_STATUSES[coreId] ? { careerStatuses: TARGET_CAREER_STATUSES[coreId] } : {}),
    },
    conditions: { minAge: event.actorMinAge ?? 18, maxAge: event.actorMaxAge },
    choices,
  };
});

const ARC_MODIFIERS: Array<Record<string, number>> = [
  { inheritance: 0.65 }, { education: 1.1 }, { relationship: 1.1 }, { business: 1.1 }, { recovery: 1.1 },
  { reinvention: 1.1 }, { inheritance: 0.7 }, { volatility: 1.15 }, { support: 1.1 }, { legacy: 1.2 },
];

export const ADAPTED_GENERATION_ARCS = GENERATION_ARCS.map((arc, index) => ({
  id: arc.id,
  name: arc.title,
  eventTopics: index % 2 === 0 ? ["family", "wealth", "family-parent"] : ["education", "career", "relationship"],
  weightMultiplier: index === 9 ? 1.3 : 1.2,
  modifiers: ARC_MODIFIERS[index] || {},
}));
